import type { PdfBlobRecord, PdfBlobRepository } from "@/domain";

import type { DatabaseInvalidationPublisher } from "./broadcast-channel";
import type { MarginaliaDatabase } from "./database";
import { toStorageWriteError } from "./storage-errors";

function assertPdfBlobRecord(record: PdfBlobRecord): void {
  if (record.contentType !== "application/pdf") {
    throw new TypeError("Stored PDF content type must be application/pdf.");
  }

  if (record.blob.size !== record.byteLength) {
    throw new TypeError("Stored PDF byteLength must match the Blob size.");
  }
}

export class DexiePdfBlobRepository implements PdfBlobRepository {
  constructor(
    private readonly db: MarginaliaDatabase,
    private readonly invalidation?: DatabaseInvalidationPublisher,
  ) {}

  get(documentId: string): Promise<PdfBlobRecord | undefined> {
    return this.db.pdfBlobs.get(documentId);
  }

  async put(record: PdfBlobRecord): Promise<void> {
    assertPdfBlobRecord(record);

    try {
      await this.db.pdfBlobs.put(record);
    } catch (error) {
      throw toStorageWriteError(error, "retain a local PDF");
    }

    this.invalidation?.publish({
      type: "document-changed",
      documentId: record.documentId,
    });
  }

  async delete(documentId: string): Promise<void> {
    try {
      await this.db.pdfBlobs.delete(documentId);
    } catch (error) {
      throw toStorageWriteError(error, "remove a retained PDF");
    }

    this.invalidation?.publish({
      type: "document-changed",
      documentId,
    });
  }
}
